import React from 'react'
import styles from '../../styles'
import { FieldRenderProps } from 'react-final-form'
import { FormattedMessageModel } from './types/fieldsModel'
import { useIntl } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import { TextField } from '@mui/material'

const useStyles = makeStyles()(
	(theme) => (
		{
			...styles(theme),
			formTextFieldContainer: {
				'& .MuiTextField-root': {
					backgroundColor: theme.palette.blue['5'],
					borderRadius: 40,
					minWidth: 120,
					width: '100%',
					padding: '10px 20px',
					boxSizing: 'border-box',
					'& input': {
						color: theme.palette.blue.main,
						...styles(theme).text4
					}
				},
				'& .MuiInputBase-underline:before, & .MuiInputBase-underline:after': {
					display: 'none'
				}
			},
			rectangleFormTextFieldContainer: {
				width: '100%',
				'& .MuiTextField-root': {
					backgroundColor: 'white',
					minWidth: 120,
					maxWidth: 371,
					height: 48,
					width: '100%',
					padding: '10px 20px',
					boxSizing: 'border-box',
					'& input': {
						color: theme.palette.blue.main,
						...styles(theme).text4,
						fontSize: 18
					}
				},
				'& .MuiInputBase-underline:before, & .MuiInputBase-underline:after': {
					display: 'none'
				}
			},
			textFieldStyle: {
				width: '100%',
				boxSizing: 'border-box',

				'& label': {
					marginBottom: 5,
					...styles(theme).text7,
					fontSize: 11
				},

				'& input': {
					padding: 0
				}
			},
			errorStyle: {
				color: '#B92D00',
				fontSize: '10px'
			},
			errorColor: {
				color: '#B92D00 !important'
			},
			errorBorder: {
				border: '1px solid #B92D00'
			},
			disabledStyle: {
				'& input': {
					opacity: '0.5 !important'
				}
			}
		}
	)
)

interface AcceptingProps {
	id: string,
	label?: string,
	placeholder?: FormattedMessageModel,
	multiline?: boolean,
	row?: number,
	maxLength?: number,
	type?: string,
	disablePaste: boolean,
	rectangle?: boolean,
	disabled?: boolean
}

type FormTextFieldProps =
	FieldRenderProps<string>
	& AcceptingProps

const FormTextField: React.FC<FormTextFieldProps> = (
	{
		input,
		meta: {
			touched,
			error
		},
		id,
		label,
		placeholder,
		multiline,
		row,
		maxLength,
		disablePaste = false,
		rectangle = false,
		disabled,
		readOnly
	}
) => {
	const { classes, cx } = useStyles()
	const intl = useIntl()

	const labelClass = cx(
		classes.text3_opacity,
		classes.colorBlue,
		{
			[classes.errorColor]: touched && error
		}
	)

	const textFieldClass = cx(
		classes.textFieldStyle,
		{
			[classes.errorBorder]: touched && error,
			[classes.disabledStyle]: disabled
		}
	)

	const checkKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === '"') {
			e.preventDefault()
		}
	}

	return (
		<div className={rectangle ? classes.rectangleFormTextFieldContainer : classes.formTextFieldContainer}>
			<span className={labelClass}>
				{label}
			</span>

			<TextField
				id={id}
				color="primary"
				onKeyDown={checkKeyDown}
				disabled={disabled}
				className={textFieldClass}
				multiline={multiline}
				rows={row}
				inputProps={{ maxLength: maxLength, readOnly: readOnly }}
				placeholder={placeholder ?
					intl.formatMessage(
						{
							id: placeholder.id,
							defaultMessage: placeholder.defaultMessage,
							description: placeholder.description
						},
						placeholder.values
					) : ''
				}
				onPaste={(e) => disablePaste && e.preventDefault()}
				{...input}
			/>

			{touched && error && <span className={classes.errorStyle}>{error}</span>}
		</div>
	)
}

export default FormTextField
